import {
  compileProject,
  compileOrgDiff,
  renderOrgTreeView,
  renderTeamDependencyGraph,
  collectAllTeamIds,
  type Diagnostic,
  type Warning,
  type ViewPath,
  type FileSystemProvider,
  type DisplayMode,
  type DiagramTheme,
  type OrganizationBlock,
  type ResolvedStyles,
  type TeamDependencyReport,
} from "@karasu-tools/core";
import { useMemo } from "react";
import { useEmptyStateLabels } from "../i18n/use-empty-state-labels.js";
import { useAnnotationBadgeLabels } from "../i18n/use-annotation-badge-labels.js";
import { computeViewResultFingerprint } from "./result-fingerprint.js";
import {
  useDebouncedCompile,
  resolveBaseAndDiff,
  type CompileOutcome,
} from "./useDebouncedCompile.js";
import { useCollapsibleSet } from "./useCollapsibleSet.js";

interface OrgViewState {
  orgSvg: string;
  orgDiagnostics: Diagnostic[];
  orgWarnings: Warning[];
  nodePathIndex: Map<string, string[]>;
  organizations: OrganizationBlock[];
  ownerIndex: Map<string, string>;
  styles: ResolvedStyles | undefined;
  /**
   * Team dependencies derived from `owns` × the logical edges (#2636). Comes
   * off the same org compile as `organizations`, so the graph cannot show a
   * different model than the tree beside it.
   */
  teamDependencies: TeamDependencyReport;
}

const EMPTY_TEAM_DEPENDENCIES: TeamDependencyReport = {
  teams: [],
  dependencies: [],
  unowned: [],
  overlaps: [],
};

export function useOrgView(
  entryPath: string | null,
  fs: FileSystemProvider | null,
  viewPath: ViewPath = [],
  displayMode?: DisplayMode,
  compareEntryPath: string | null = null,
  compareFs: FileSystemProvider | null = null,
  theme?: DiagramTheme,
  /**
   * Whether the org tab's dependency mode is drawn. The SVG is built only then:
   * `OrgCompileResult.teamDependencies` derives lazily, so leaving this false
   * means the join never runs at all on a compile nobody is looking at.
   */
  teamDependenciesOpen = false,
): OrgViewState & {
  recompile: () => void;
  expandedTeamIds: ReadonlySet<string>;
  toggleTeamExpand: (teamId: string) => void;
  orgTreeSvg: string;
  orgTreeExportSvg: string;
  teamDependencySvg: string;
} {
  const { set: expandedTeamIds, toggle: toggleTeamExpand } = useCollapsibleSet<string>();

  const emptyStateLabels = useEmptyStateLabels();
  const annotationBadgeLabels = useAnnotationBadgeLabels();

  // Structural key for `viewPath` so a fresh `[]` from `SET_ACTIVE_VIEW` does
  // not restart the in-flight debounce when the previous value was also empty.
  // Mirrors the same fix in `useSystemView`. See #1171.
  const viewPathKey = viewPath.join("/");
  const currentKey = `${entryPath}:org:${viewPathKey}:cmp=${compareEntryPath ?? ""}`;

  const compile = async (): Promise<CompileOutcome<OrgViewState> | null> => {
    if (!entryPath || !fs) return null;

    const basePromise = compileProject(entryPath, fs, {
      diagramType: "org",
      viewPath,
      displayMode,
      emptyStateLabels,
      annotationBadgeLabels,
      theme,
    });

    // Diff-mode replaces only the SVG and diagnostics; the org metadata
    // (organizations / nodePathIndex / styles / warnings) comes from the base.
    const { base, svg, diagnostics } = await resolveBaseAndDiff(
      basePromise,
      compareEntryPath
        ? compileOrgDiff({
            beforeEntryPath: compareEntryPath,
            afterEntryPath: entryPath,
            fs: compareFs ?? fs,
            viewPath,
            displayMode,
            emptyStateLabels,
            annotationBadgeLabels,
            theme,
          })
        : null,
    );
    if (base.diagramType !== "org") return null;
    const orgBase = base;

    return {
      // Org's fingerprint intentionally omits nodeMetadata (it carries none).
      fingerprint: computeViewResultFingerprint({ svg, warnings: orgBase.warnings, diagnostics }),
      // On error keep the prior org metadata (only swap in the stale SVG +
      // fresh diagnostics), matching the prior per-hook behavior.
      errorState: (svgToShow, prev) => ({
        orgSvg: svgToShow,
        orgDiagnostics: diagnostics,
        orgWarnings: prev.orgWarnings,
        nodePathIndex: prev.nodePathIndex,
        organizations: prev.organizations,
        ownerIndex: prev.ownerIndex,
        styles: prev.styles,
        teamDependencies: prev.teamDependencies,
      }),
      okState: () => ({
        orgSvg: svg,
        orgDiagnostics: diagnostics,
        orgWarnings: orgBase.warnings,
        nodePathIndex: orgBase.nodePathIndex,
        organizations: orgBase.organizations,
        ownerIndex: orgBase.ownerIndex,
        styles: orgBase.styles,
        teamDependencies: orgBase.teamDependencies,
      }),
      // Org names its state fields differently (orgSvg / orgDiagnostics); the
      // selectors bridge that, so the scaffold needs no per-view field names.
      getSvg: (s) => s.orgSvg,
      getDiagnostics: (s) => s.orgDiagnostics,
    };
  };

  const state = useDebouncedCompile<OrgViewState>({
    active: !!entryPath && !!fs,
    currentKey,
    initialState: {
      orgSvg: "",
      orgDiagnostics: [],
      orgWarnings: [],
      nodePathIndex: new Map(),
      organizations: [],
      ownerIndex: new Map(),
      styles: undefined,
      teamDependencies: EMPTY_TEAM_DEPENDENCIES,
    },
    compile,
    onError: (prev) => ({
      ...prev,
      orgDiagnostics: [{ severity: "error", code: "app-org-parse-error", params: {} }],
    }),
    deps: [
      entryPath,
      fs,
      viewPathKey,
      displayMode,
      theme,
      compareEntryPath,
      compareFs,
      emptyStateLabels,
      annotationBadgeLabels,
    ],
  });
  const { recompile } = state;

  const orgTreeSvg =
    state.organizations.length > 0
      ? renderOrgTreeView(state.organizations, expandedTeamIds, {
          styles: state.styles,
          theme,
          emptyStateLabels,
        })
      : "";

  const orgTreeExportSvg =
    state.organizations.length > 0
      ? renderOrgTreeView(state.organizations, new Set(collectAllTeamIds(state.organizations)), {
          forExport: true,
          styles: state.styles,
          theme,
          emptyStateLabels,
        })
      : "";

  // Recomputed whenever the compiled report or the theme changes, and from
  // nothing else — the report is the whole input, so there is no second source
  // to leave stale (TPL-1032). Memoized because this hook re-runs on every
  // AppShell render (each keystroke, each panel resize), and rebuilding an SVG
  // string for a tab the user may not even be on is work nothing asked for.
  //
  // The no-teams case is *not* gated here: the renderer owns it and draws the
  // empty state. Two callers each deciding what "no teams" looks like is how
  // they end up disagreeing (the toggle's own gate is a different question —
  // whether to offer the mode at all — and stays in `PreviewViewControls`).
  const teamDependencySvg = useMemo(
    () =>
      teamDependenciesOpen
        ? renderTeamDependencyGraph(state.teamDependencies, { theme, emptyStateLabels })
        : "",
    [teamDependenciesOpen, state.teamDependencies, theme, emptyStateLabels],
  );

  return {
    ...state,
    recompile,
    expandedTeamIds,
    toggleTeamExpand,
    orgTreeSvg,
    orgTreeExportSvg,
    teamDependencySvg,
  };
}
